import { useQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";

import { erc20Abi, type Address } from "viem";
import { keelVaultAbi, keelVaultFactoryAbi } from "../abi";
import { implDeClon } from "../lib/clon";
import { IMPLS_OCULTAS, esIgual, porIdCadena } from "../config/networks";
import { clientePublico } from "../lib/cliente";

const POOL_ABI = [
  // slot0 truncado a (sqrtPriceX96, tick): Slipstream devuelve 6 campos, Uniswap 7; con 2 vale para ambos
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
] as const;

const NPM_ABI = [
  { type: "function", name: "positions", stateMutability: "view", inputs: [{ type: "uint256" }],
    outputs: [{ type: "uint96" }, { type: "address" }, { type: "address" }, { type: "address" },
              { type: "uint24" }, { type: "int24" }, { type: "int24" }, { type: "uint128" },
              { type: "uint256" }, { type: "uint256" }, { type: "uint128" }, { type: "uint128" }] },
  // LAS COMISIONES QUE TODAVIA ESTAN DENTRO DE LA POSICION. No hay getter que las diga: `positions`
  // devuelve `tokensOwed0/1`, que solo se actualizan cuando alguien toca la posicion y valen 0 en
  // una que lleva dias cobrando sin que nadie la mueva. La forma que usa todo el mundo —incluida la
  // propia interfaz de Uniswap— es SIMULAR el cobro: `collect` no es `view`, pero en un `eth_call`
  // desde el dueño del NFT devuelve exactamente lo que se llevaria ahora mismo.
  { type: "function", name: "collect", stateMutability: "payable",
    inputs: [{ type: "tuple", components: [
      { name: "tokenId", type: "uint256" }, { name: "recipient", type: "address" },
      { name: "amount0Max", type: "uint128" }, { name: "amount1Max", type: "uint128" },
    ] }],
    outputs: [{ type: "uint256" }, { type: "uint256" }] },
] as const;

/** El tope de `collect`: pedir "todo lo que haya". */
const MAX_U128 = 2n ** 128n - 1n;

export type Vault = {
  direccion: Address;
  pool: Address;
  token0: Address;
  token1: Address;
  tokenId: bigint;
  /** capital aportado, en el token de referencia (el estable del par) */
  costBasisRef: bigint;
  refEsToken0: boolean;
  reserva: bigint;
  pausado: boolean;
  impl?: Address;
  /** su logica es la que publica su factory hoy? si no, se le puede ofrecer actualizar */
  alDia: boolean;
  puedeActualizar: boolean;

  // Lo que hace falta para pintar la banda en la tarjeta. Sin esto la lista enseña numeros sueltos
  // y el dueño tiene que interpretarlos para saber lo unico que le importa: si esta cobrando.
  par?: string;
  /** los dos símbolos por separado, para poder enseñar el precio en la unidad que se entiende */
  s0?: string;
  s1?: string;
  simboloRef?: string;
  decimalesRef?: number;
  /** token1 por token0, en unidades humanas */
  precio?: number;
  lo?: number;
  hi?: number;
  /** ganancias ya apartadas, en crudo. `undefined` = este vault no sabe decirlo (clon anterior al 09-ago) */
  profit0?: bigint;
  profit1?: bigint;
  /** comisiones cobradas que siguen DENTRO del NFT, sin barrer */
  pendiente0?: bigint;
  pendiente1?: bigint;
  /** decimales de cada token, para poder convertir `profit0/1` a la moneda de referencia */
  d0?: number;
  d1?: number;
  /** saldo de cada token DENTRO del propio vault (balanceOf del vault). Antes de abrir es el depósito
   *  a la espera; con posición abierta es reserva + bolsa de gas + lo cobrado sin barrer. Sirve para
   *  detectar un vault «depositado pero sin abrir» (tokenId 0 y saldo > 0), que `deposit()` NO marca
   *  en `costBasisRef` —solo `open()` lo hace— y que si no se distingue cae en «Cerradas» con dinero. */
  libre0?: bigint;
  libre1?: bigint;
};

/**
 * Los vaults del usuario en la red conectada, de TODAS sus factories.
 *
 * En la app vieja esto eran ~90 lineas repartidas entre 00-config.js y 03-vaults.js, con un
 * contador de "factories que no se pudieron consultar" porque el error se tragaba en silencio: si
 * el RPC fallaba en UNA factory, sus vaults desaparecian de la lista y la pantalla decia "Aun no
 * tienes inversiones" como si todo estuviera bien. Aqui, si una factory falla, la query falla y se
 * ve — react-query reintenta y expone `isError`.
 */
export function useVaults() {
  const { address, chainId } = useAccount();

  return useQuery({
    queryKey: ["vaults", chainId, address],
    enabled: !!address && !!chainId && !!porIdCadena(chainId),
    queryFn: async (): Promise<Vault[]> => {
      const red = porIdCadena(chainId!)!;
      const client = clientePublico(chainId!);
      if (!client) throw new Error("sin cliente RPC para esta red");

      // 1. las direcciones, factory a factory
      const listas = await Promise.all(
        red.factories.map((factory) =>
          client.readContract({
            address: factory,
            abi: keelVaultFactoryAbi,
            functionName: "vaultsOf",
            args: [address!],
          })
        )
      );
      const direcciones = [
        ...new Set(listas.flat().map((d) => d.toLowerCase())),
      ] as Address[];
      if (direcciones.length === 0) return [];

      // 2. la logica que publica cada factory hoy, para saber a quien ofrecer actualizar
      const logicasPublicadas = await Promise.all(
        red.factories.map((factory) =>
          client.readContract({
            address: factory,
            abi: keelVaultFactoryAbi,
            functionName: "rebalancer",
          })
        )
      );

      // 3. el estado de cada vault. Un multicall POR VAULT, con la lista de lecturas escrita
      //    literal: asi viem infiere la tupla de retorno campo a campo (Address, bigint, bool…)
      //    desde el ABI. Aplanar los N vaults en un solo multicall obligaria a un `as` a mano
      //    sobre el resultado, y ese `as` es exactamente el agujero que veniamos a tapar: una
      //    mentira al compilador que no falla hasta que alguien cambia el orden de los campos.
      const ocultas = IMPLS_OCULTAS[red.clave];

      const vaults = await Promise.all(
        direcciones.map(async (direccion): Promise<Vault> => {
          const comun = { address: direccion, abi: keelVaultAbi } as const;
          const [
            [pool, token0, token1, tokenId, costBasisRef, recoverToken0, reserve, paused, rebalancer, npm],
            bytecode,
          ] = await Promise.all([
            client.multicall({
              allowFailure: false,
              contracts: [
                { ...comun, functionName: "pool" },
                { ...comun, functionName: "token0" },
                { ...comun, functionName: "token1" },
                { ...comun, functionName: "tokenId" },
                { ...comun, functionName: "costBasisRef" },
                { ...comun, functionName: "recoverToken0" },
                { ...comun, functionName: "reserve" },
                { ...comun, functionName: "paused" },
                { ...comun, functionName: "rebalancer" },
                { ...comun, functionName: "npm" },
              ],
            }),
            client.getCode({ address: direccion }),
          ]);

          // LO GANADO, en el mismo viaje. Van en un multicall aparte y con `allowFailure` porque los
          // clones anteriores al 2026-08-09 no tienen `profit0/1`: ahí la llamada revierte, y si
          // fuera parte del lote de arriba tumbaría la fila entera de un vault que por lo demás se
          // lee perfectamente. Lo que no se puede leer se queda sin cifra, no sin tarjeta.
          // De paso, el saldo de cada token DENTRO del vault: para ver si hay un depósito esperando a
          // abrirse. Van en el mismo lote con `allowFailure` —un balanceOf no puede tumbar la fila—.
          const [p0, p1, b0, b1] = await client
            .multicall({
              allowFailure: true,
              contracts: [
                { ...comun, functionName: "profit0" },
                { ...comun, functionName: "profit1" },
                { address: token0, abi: erc20Abi, functionName: "balanceOf", args: [direccion] },
                { address: token1, abi: erc20Abi, functionName: "balanceOf", args: [direccion] },
              ],
            })
            .then((r) => r.map((x) => (x.status === "success" ? (x.result as bigint) : undefined)))
            .catch(() => [undefined, undefined, undefined, undefined]);

          // Simbolos, decimales, precio y rango: lo que pide la banda de la tarjeta. Si algo de
          // esto falla, la tarjeta sale sin banda pero con sus numeros — nunca en blanco.
          let extra: Partial<Vault> = {};
          try {
            const [s0, d0, s1, d1, slot0] = await client.multicall({
              allowFailure: false,
              contracts: [
                { address: token0, abi: erc20Abi, functionName: "symbol" },
                { address: token0, abi: erc20Abi, functionName: "decimals" },
                { address: token1, abi: erc20Abi, functionName: "symbol" },
                { address: token1, abi: erc20Abi, functionName: "decimals" },
                { address: pool, abi: POOL_ABI, functionName: "slot0" },
              ],
            });
            const sp = Number(slot0[0]) / 2 ** 96;
            extra = {
              par: `${s0}/${s1}`,
              // Los dos símbolos SUELTOS, además del par ya montado: la tarjeta necesita saber
              // cuál es cuál para enseñar el precio al derecho. Partir `par` por la barra funciona
              // hasta el día que un token lleve una en el símbolo.
              s0, s1,
              simboloRef: recoverToken0 ? s0 : s1,
              decimalesRef: recoverToken0 ? d0 : d1,
              precio: sp * sp * 10 ** (d0 - d1),
              d0, d1,
            };
            if (tokenId !== 0n) {
              const pos = await client.readContract({
                address: npm, abi: NPM_ABI, functionName: "positions", args: [tokenId],
              });
              extra.lo = 1.0001 ** pos[5] * 10 ** (d0 - d1);
              extra.hi = 1.0001 ** pos[6] * 10 ** (d0 - d1);

              // Lo que la posición lleva cobrado y AÚN NO SE HA BARRIDO. Sin esto, una piscina
              // recién abierta enseña «ganado 0» aunque esté cobrando desde el primer minuto:
              // `profit0/1` solo cuenta lo que ya salió del NFT, y eso pasa en el primer reajuste
              // o cuando el dueño lo pide. Dos días cobrando y un cero en pantalla no es un dato,
              // es una pregunta.
              const pend = await client
                .simulateContract({
                  address: npm, abi: NPM_ABI, functionName: "collect",
                  args: [{ tokenId, recipient: direccion, amount0Max: MAX_U128, amount1Max: MAX_U128 }],
                  account: direccion, // el dueño del NFT es el propio vault
                })
                .then((r) => r.result as readonly [bigint, bigint])
                .catch(() => null); // un RPC que no deja simular no puede dejar la fila sin datos
              if (pend) {
                extra.pendiente0 = pend[0];
                extra.pendiente1 = pend[1];
              }
            }
          } catch { /* la tarjeta sale sin banda */ }

          const alDia = logicasPublicadas.some((l) => esIgual(l, rebalancer));
          return {
            direccion,
            pool,
            token0,
            token1,
            tokenId,
            costBasisRef,
            refEsToken0: recoverToken0,
            reserva: reserve,
            pausado: paused,
            impl: implDeClon(bytecode),
            alDia,
            profit0: p0,
            profit1: p1,
            libre0: b0,
            libre1: b1,
            ...extra,
            // Solo tiene sentido ofrecerlo si hay algo mas nuevo publicado. Que el vault ENTIENDA
            // `upgradeRebalancer` se comprueba aparte, simulando la llamada: los clones de
            // implementaciones anteriores al 2026-08-12 no la tienen.
            puedeActualizar: !alDia,
          };
        })
      );

      const visibles = vaults.filter(
        (v) => !v.impl || !ocultas.some((o) => esIgual(o, v.impl))
      );

      return visibles;
    },
  });
}
